// Copyright (c) 2026 Optimum Healthcare IT. All rights reserved.
// Author: Frank Scalzo <Optimum Healthcare IT>
// License: proprietary
// provenance-id: 77c9717e-70db-41ca-9a55-f972159abfcc

import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Paper, Typography, Stack, TextField, MenuItem, Chip, Tooltip, Button,
  Divider, FormControlLabel, Switch, Alert,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { PICKLISTS, resolveDrTier } from './schema';
import { printElementToPdf } from './exports';
import { buildNameIndex, lookupName } from './nameIndex';
import store from './store';

// ---------------------------------------------------------------------------
// Migration Wave Roadmap — the steering-committee artifact.
//
// Applications are laid out left-to-right as migration waves (fields.cmWave,
// "CM: Selected for Wave"). The point of the view is not the pretty timeline:
// it is the DEPENDENCY RISK analysis. An app that integrates with another app
// scheduled in a LATER wave will lose its integration partner at cutover (or
// have to run a cross-site interface for months). That is the classic wave-
// planning trap, and it is only visible when you cross the integration list
// against the wave assignment — which is exactly what this component does.
// ---------------------------------------------------------------------------

const NOT_SCHEDULED = 'Not Scheduled';

// Optimum palette.
const NAVY = '#021F4B';
const BLUE = '#0D5B9A';
const GREY = '#5C6B7A';

// Disposition (6R) → card color. Same six values back both `sixRClassification`
// (Portfolio Strategy) and `cmDisposition` (Cloud Transformation), so one map
// serves whichever field the engagement populated.
const DISPOSITION_COLORS = {
  Rehost: '#0D5B9A',
  Replatform: '#00838F',
  Repurchase: '#6A1B9A',
  Refactor: '#EF6C00',
  Retire: '#C62828',
  Retain: '#5C6B7A',
};
const UNSET = '#90A4AE';

const waveOf = (a) => (a.fields?.cmWave || '').trim() || NOT_SCHEDULED;
// Portfolio Strategy wins when both are set; it is the reviewed value.
const dispositionOf = (a) => a.fields?.sixRClassification || a.fields?.cmDisposition || '';
// DR Tier is recorded two ways ("DR Tier: 2" as a tag, "Tier 2" as a field).
// resolveDrTier() is the single reconciler — reading either vocabulary raw here
// puts both spellings of the same tier in the filter list as separate options.
const drTierOf = (a) => resolveDrTier(a);
const effortOf = (a) => a.fields?.cmEffort || '';

// ---------------------------------------------------------------------------
// Dependency ordering. "Not Scheduled" is the backlog, not a late wave, so it
// has to be read as UNKNOWN. An integration partner nobody has scheduled yet is
// a GAP in the plan; it is not evidence the plan is out of order. Comparing
// wave indexes blindly makes every unscheduled partner look like a violation —
// and early in an engagement almost everything is unscheduled, which buries the
// handful of genuine ordering problems this view exists to find.
//
//   'ordered'     → nothing to report (target migrates with or before the source)
//   'later'       → genuine ordering violation, both sides scheduled
//   'unscheduled' → one side has no wave yet: a data gap, reported separately
//
// Kept identical to the copy in ReadinessView.js — the two screens must agree.
// ---------------------------------------------------------------------------
const classifyDependency = (fromWave, toWave, waveIndex) => {
  if (fromWave === NOT_SCHEDULED || toWave === NOT_SCHEDULED) return 'unscheduled';
  return waveIndex[toWave] > waveIndex[fromWave] ? 'later' : 'ordered';
};

// "Small: 1-2 months" → "Small". Keeps chips readable in a dense column.
const shortEffort = (e) => (e ? String(e).split(':')[0].trim() : '—');

export default function RoadmapView({ tenantId, onOpen }) {
  const [apps, setApps] = useState([]);
  const [disposition, setDisposition] = useState('All');
  const [drTier, setDrTier] = useState('All');
  const [risksOnly, setRisksOnly] = useState(false);

  useEffect(() => { store.listFactSheets(tenantId, { type: 'Application' }).then(setApps); }, [tenantId]);

  // Wave columns in plan order. Anything the tenant invented beyond the
  // standard picklist is appended before "Not Scheduled", which always sits
  // last — it is the backlog, not a wave.
  const waveOrder = useMemo(() => {
    const known = PICKLISTS.migrationWave.filter((w) => w !== NOT_SCHEDULED);
    const extra = [...new Set(apps.map(waveOf))]
      .filter((w) => w !== NOT_SCHEDULED && !known.includes(w))
      .sort();
    return [...known, ...extra, NOT_SCHEDULED];
  }, [apps]);

  const waveIndex = useMemo(() => {
    const m = {};
    waveOrder.forEach((w, i) => { m[w] = i; });
    return m;
  }, [waveOrder]);

  // --- Dependency risk -----------------------------------------------------
  // Computed over the FULL inventory, never the filtered set: a risk does not
  // stop existing because the user filtered the dependency out of view.
  //
  // Two DIFFERENT findings come out of this pass and are never merged:
  //   risks — both apps are scheduled and the target migrates later. Real.
  //   gaps  — one side is still unscheduled. A hole in the plan, not a finding
  //           about the architecture, so it is counted and worded separately.
  const { risksByApp, allRisks, gapsByApp, allGaps } = useMemo(() => {
    // Tenant-scoped: in the master workspace `apps` spans every client, so a
    // bare-name key made one client's app "depend on" another client's — and
    // rendered it as a link straight into their fact sheet (:382, :421).
    const byName = buildNameIndex(apps);

    const map = new Map();
    const list = [];
    const gapMap = new Map();
    const gapList = [];
    apps.forEach((a) => {
      const fromWave = waveOf(a);
      (a.integrations || []).forEach((it, i) => {
        const target = lookupName(byName, a, it?.to);
        // Only in-inventory applications can be "scheduled later" — an external
        // endpoint (vendor SaaS, identity provider) has no wave to compare.
        if (!target || target.id === a.id) return;
        const toWave = waveOf(target);
        const kind = classifyDependency(fromWave, toWave, waveIndex);
        if (kind === 'ordered') return;
        const entry = {
          id: `${a.id}->${target.id}#${i}`, // index keeps the React key unique for duplicate targets
          fromId: a.id, fromName: a.name, fromWave,
          toId: target.id, toName: target.name, toWave,
          via: it.via || '', protocol: it.protocol || '', port: it.port || '',
          dataType: it.dataType || '', direction: it.direction || '',
          kind,
        };
        const [targetList, targetMap] = kind === 'later' ? [list, map] : [gapList, gapMap];
        targetList.push(entry);
        if (!targetMap.has(a.id)) targetMap.set(a.id, []);
        targetMap.get(a.id).push(entry);
      });
    });
    // Earliest source wave first — that is the order the cutover team hits them.
    // NOT_SCHEDULED sorts last here purely for display; it is never compared as
    // a wave position (see classifyDependency).
    const bySourceWave = (x, y) => (waveIndex[x.fromWave] - waveIndex[y.fromWave])
      || x.fromName.localeCompare(y.fromName);
    list.sort(bySourceWave);
    gapList.sort(bySourceWave);
    return { risksByApp: map, allRisks: list, gapsByApp: gapMap, allGaps: gapList };
  }, [apps, waveIndex]);

  const drTierOptions = useMemo(
    () => [...new Set(apps.map(drTierOf).filter(Boolean))].sort(),
    [apps]
  );

  const filtered = useMemo(() => apps.filter((a) => {
    if (disposition !== 'All' && dispositionOf(a) !== disposition) return false;
    if (drTier !== 'All' && drTierOf(a) !== drTier) return false;
    if (risksOnly && !risksByApp.has(a.id)) return false;
    return true;
  }), [apps, disposition, drTier, risksOnly, risksByApp]);

  // wave → { items, phi, effortMix }
  const columns = useMemo(() => waveOrder.map((wave) => {
    const items = filtered.filter((a) => waveOf(a) === wave);
    const effortMix = {};
    items.forEach((a) => {
      const k = shortEffort(effortOf(a));
      effortMix[k] = (effortMix[k] || 0) + 1;
    });
    return {
      wave,
      items,
      phi: items.filter((a) => !!a.fields?.phi).length,
      risky: items.filter((a) => risksByApp.has(a.id)).length,
      unscheduledDeps: items.filter((a) => gapsByApp.has(a.id)).length,
      effortMix: Object.entries(effortMix).sort((x, y) => y[1] - x[1]),
    };
  }), [waveOrder, filtered, risksByApp, gapsByApp]);

  const visibleRisks = useMemo(() => {
    const ids = new Set(filtered.map((a) => a.id));
    return allRisks.filter((r) => ids.has(r.fromId));
  }, [allRisks, filtered]);

  const visibleGaps = useMemo(() => {
    const ids = new Set(filtered.map((a) => a.id));
    return allGaps.filter((r) => ids.has(r.fromId));
  }, [allGaps, filtered]);

  const anyWaveSet = apps.some((a) => (a.fields?.cmWave || '').trim());

  return (
    <Box sx={{ p: 2 }}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2, flexWrap: 'wrap', rowGap: 1 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, color: NAVY }}>Migration Wave Roadmap</Typography>

        <TextField select size="small" label="Disposition" value={disposition}
          onChange={(e) => setDisposition(e.target.value)} sx={{ minWidth: 160 }}>
          <MenuItem value="All">All dispositions</MenuItem>
          {PICKLISTS.sixR.map((d) => <MenuItem key={d} value={d}>{d}</MenuItem>)}
        </TextField>

        <TextField select size="small" label="DR Tier" value={drTier}
          onChange={(e) => setDrTier(e.target.value)} sx={{ minWidth: 150 }}>
          <MenuItem value="All">All DR tiers</MenuItem>
          {drTierOptions.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
        </TextField>

        <FormControlLabel
          control={<Switch size="small" checked={risksOnly} onChange={(e) => setRisksOnly(e.target.checked)} />}
          label={<Typography sx={{ fontSize: 13, color: GREY }}>Dependency risks only</Typography>}
        />

        <Box sx={{ flex: 1 }} />
        <Button startIcon={<DownloadIcon />} variant="outlined"
          onClick={() => printElementToPdf('roadmap-print', 'Migration Wave Roadmap')}>PDF</Button>
      </Stack>

      {!anyWaveSet ? (
        <Alert severity="info" sx={{ maxWidth: 760 }}>
          <Typography sx={{ fontWeight: 700, color: 'sectionHeader.main', mb: 0.5 }}>No migration waves assigned yet</Typography>
          Waves are set per application on the fact sheet, under <b>Cloud Transformation → Cloud Migration
          General</b>, in the field <b>“CM: Selected for Wave”</b> ({PICKLISTS.migrationWave.join(', ')}).
          Assign waves to at least one application and the roadmap — including the cross-wave dependency
          analysis — will populate here.
        </Alert>
      ) : (
        <div id="roadmap-print">
          {/* Timeline rail: purely visual cue that the columns read left→right. */}
          <Box sx={{
            display: 'flex', alignItems: 'center', gap: 1, mb: 1,
            color: GREY, fontSize: 12, fontWeight: 600,
          }}>
            <span>Migration timeline</span>
            <Box sx={{ flex: 1, height: 4, borderRadius: 2, background: `linear-gradient(90deg, ${BLUE}, #cfd8dc)` }} />
            <ArrowForwardIcon sx={{ fontSize: 16 }} />
          </Box>

          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', overflowX: 'auto', pb: 1 }}>
            {columns.map((col) => (
              <Paper key={col.wave} sx={{
                p: 1.25, minWidth: 260, flex: '1 1 260px',
                bgcolor: col.wave === NOT_SCHEDULED ? '#fafafa' : '#fff',
                borderTop: `3px solid ${col.wave === NOT_SCHEDULED ? '#B0BEC5' : BLUE}`,
              }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography sx={{ fontWeight: 700, color: 'sectionHeader.main', fontSize: 14 }}>{col.wave}</Typography>
                  <Chip size="small" label={`${col.items.length} apps`} sx={{ height: 18, fontSize: 11 }} />
                </Stack>

                <Stack direction="row" spacing={0.5} sx={{ mt: 0.75, flexWrap: 'wrap', rowGap: 0.5 }}>
                  {col.effortMix.map(([lbl, n]) => (
                    <Chip key={lbl} size="small" variant="outlined" label={`${lbl} ${n}`}
                      sx={{ height: 18, fontSize: 10, borderColor: '#cfd8dc', color: GREY }} />
                  ))}
                  {col.phi > 0 && (
                    <Chip size="small" label={`PHI ${col.phi}`}
                      sx={{ height: 18, fontSize: 10, bgcolor: '#EDE7F6', color: '#4527A0', fontWeight: 700 }} />
                  )}
                  {col.risky > 0 && (
                    <Tooltip title={`${col.risky} app(s) here integrate with an application scheduled in a later wave`}>
                      <Chip size="small" label={`Out of order ${col.risky}`}
                        sx={{ height: 18, fontSize: 10, bgcolor: '#FFF3E0', color: '#E65100', fontWeight: 700 }} />
                    </Tooltip>
                  )}
                  {col.unscheduledDeps > 0 && (
                    <Tooltip title={`${col.unscheduledDeps} app(s) here integrate with an application that has no wave yet — a planning gap, not an ordering violation`}>
                      <Chip size="small" label={`Dep. unscheduled ${col.unscheduledDeps}`}
                        sx={{ height: 18, fontSize: 10, bgcolor: '#eceff1', color: GREY, fontWeight: 700 }} />
                    </Tooltip>
                  )}
                </Stack>

                <Divider sx={{ my: 1 }} />

                {col.items.length === 0 && (
                  <Typography sx={{ fontSize: 12, color: '#aaa', py: 1 }}>No applications</Typography>
                )}

                <Stack spacing={0.75}>
                  {col.items.map((a) => {
                    const disp = dispositionOf(a);
                    const color = DISPOSITION_COLORS[disp] || UNSET;
                    const risks = risksByApp.get(a.id) || [];
                    const gaps = gapsByApp.get(a.id) || [];
                    const tier = drTierOf(a);
                    return (
                      <Paper key={a.id} elevation={0} onClick={() => onOpen(a.id)}
                        sx={{
                          p: 0.75, cursor: 'pointer', border: '1px solid #e0e0e0',
                          borderLeft: `4px solid ${color}`,
                          '&:hover': { bgcolor: '#f5f8fc', borderColor: BLUE },
                        }}>
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                          <Typography sx={{
                            fontSize: 12, fontWeight: 700, color: NAVY, flex: 1,
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                          }}>
                            {a.name}
                          </Typography>
                          {risks.length > 0 && (
                            <Tooltip title={`Out of order: ${risks.map((r) => `depends on ${r.toName} (${r.toWave})`).join(' · ')}`}>
                              <WarningAmberIcon sx={{ fontSize: 16, color: '#E65100' }} />
                            </Tooltip>
                          )}
                          {risks.length === 0 && gaps.length > 0 && (
                            <Tooltip title={`Not yet scheduled: ${gaps.map((r) => `depends on ${r.toName} (${r.toWave})`).join(' · ')}`}>
                              <HelpOutlineIcon sx={{ fontSize: 16, color: GREY }} />
                            </Tooltip>
                          )}
                        </Stack>
                        <Stack direction="row" spacing={0.5} sx={{ mt: 0.5, flexWrap: 'wrap', rowGap: 0.5 }}>
                          <Chip size="small" label={disp || 'No disposition'}
                            sx={{ height: 17, fontSize: 10, bgcolor: color, color: '#fff', fontWeight: 600 }} />
                          {tier && (
                            <Chip size="small" variant="outlined" label={tier}
                              sx={{ height: 17, fontSize: 10, borderColor: '#cfd8dc', color: GREY }} />
                          )}
                          {effortOf(a) && (
                            <Tooltip title={effortOf(a)}>
                              <Chip size="small" variant="outlined" label={shortEffort(effortOf(a))}
                                sx={{ height: 17, fontSize: 10, borderColor: '#cfd8dc', color: GREY }} />
                            </Tooltip>
                          )}
                        </Stack>
                      </Paper>
                    );
                  })}
                </Stack>
              </Paper>
            ))}
          </Box>

          {/* --- Dependency panel — the reason this view exists ---------------
              Two counts, never one. An ordering violation is a finding about
              the plan; an unscheduled partner is a hole in the data. Adding
              them together would let missing data masquerade as architecture
              risk, which is exactly what buried the real violations before. */}
          <Paper sx={{ mt: 2, p: 1.5 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1, flexWrap: 'wrap', rowGap: 0.5 }}>
              <WarningAmberIcon sx={{ fontSize: 18, color: '#E65100' }} />
              <Typography sx={{ fontWeight: 700, color: 'sectionHeader.main', fontSize: 14 }}>
                Cross-wave dependencies
              </Typography>
              <Chip size="small" label={`${visibleRisks.length} genuinely out of order`}
                sx={{ height: 19, fontSize: 11, fontWeight: 700, bgcolor: '#FFF3E0', color: '#E65100' }} />
              <Chip size="small" label={`${visibleGaps.length} not yet scheduled`}
                sx={{ height: 19, fontSize: 11, fontWeight: 700, bgcolor: '#eceff1', color: GREY }} />
            </Stack>

            {visibleRisks.length === 0 ? (
              <Typography sx={{ fontSize: 12, color: GREY }}>
                No dependencies are genuinely out of order in the current selection: every integration target
                that exists in this inventory <b>and has a wave</b> migrates in the same wave or earlier.
              </Typography>
            ) : (
              <>
                <Alert severity="warning" sx={{ mb: 1, py: 0.25 }}>
                  <b>{visibleRisks.length} genuinely out of order.</b> Both applications are scheduled and the
                  integration partner migrates in a later wave. Each one needs a cutover plan — keep the
                  interface routed back on-premises, co-schedule the pair, or accept a temporary cross-site
                  data flow.
                </Alert>
                <Stack divider={<Divider flexItem />} spacing={0}>
                  {visibleRisks.map((r) => (
                    <Stack key={r.id} direction="row" alignItems="center" spacing={1} sx={{ py: 0.6, flexWrap: 'wrap', rowGap: 0.5 }}>
                      <Box component="span" onClick={() => onOpen(r.fromId)}
                        sx={{ fontSize: 12, fontWeight: 700, color: BLUE, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}>
                        {r.fromName}
                      </Box>
                      <Chip size="small" label={r.fromWave} sx={{ height: 17, fontSize: 10 }} />
                      <Typography component="span" sx={{ fontSize: 12, color: GREY }}>depends on</Typography>
                      <Box component="span" onClick={() => onOpen(r.toId)}
                        sx={{ fontSize: 12, fontWeight: 700, color: BLUE, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}>
                        {r.toName}
                      </Box>
                      <Chip size="small" label={r.toWave}
                        sx={{ height: 17, fontSize: 10, bgcolor: '#FFF3E0', color: '#E65100', fontWeight: 700 }} />
                      <Box sx={{ flex: 1 }} />
                      <Typography component="span" sx={{ fontSize: 11, color: '#90A4AE' }}>
                        {[r.via, r.protocol, r.port, r.dataType].filter(Boolean).join(' · ') || 'integration'}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              </>
            )}

            {visibleGaps.length > 0 && (
              <>
                <Divider sx={{ my: 1.5 }} />
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                  <HelpOutlineIcon sx={{ fontSize: 18, color: GREY }} />
                  <Typography sx={{ fontWeight: 700, color: 'sectionHeader.main', fontSize: 13 }}>
                    {visibleGaps.length} {visibleGaps.length === 1 ? 'dependency' : 'dependencies'} not yet scheduled
                  </Typography>
                </Stack>
                <Alert severity="info" sx={{ mb: 1, py: 0.25 }}>
                  One side of each pair below has no wave assigned, so the ordering is <b>unknown</b>, not
                  wrong. These are gaps in the plan to close, not cutover risks — assign the waves and any
                  that turn out to be genuinely out of order will move to the list above.
                </Alert>
                <Stack divider={<Divider flexItem />} spacing={0}>
                  {visibleGaps.map((r) => (
                    <Stack key={r.id} direction="row" alignItems="center" spacing={1} sx={{ py: 0.6, flexWrap: 'wrap', rowGap: 0.5 }}>
                      <Box component="span" onClick={() => onOpen(r.fromId)}
                        sx={{ fontSize: 12, fontWeight: 700, color: BLUE, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}>
                        {r.fromName}
                      </Box>
                      <Chip size="small" label={r.fromWave} sx={{ height: 17, fontSize: 10 }} />
                      <Typography component="span" sx={{ fontSize: 12, color: GREY }}>depends on</Typography>
                      <Box component="span" onClick={() => onOpen(r.toId)}
                        sx={{ fontSize: 12, fontWeight: 700, color: BLUE, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}>
                        {r.toName}
                      </Box>
                      <Chip size="small" label={r.toWave}
                        sx={{ height: 17, fontSize: 10, bgcolor: '#eceff1', color: GREY, fontWeight: 700 }} />
                      <Box sx={{ flex: 1 }} />
                      <Typography component="span" sx={{ fontSize: 11, color: '#90A4AE' }}>
                        {[r.via, r.protocol, r.port, r.dataType].filter(Boolean).join(' · ') || 'integration'}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              </>
            )}
          </Paper>
        </div>
      )}
    </Box>
  );
}
